/**
 * Payment detail slice — data for the /payment page.
 *
 * - fetchPaymentDetail: POST /payments/detail via beaconApi, mapped to the
 *   PaymentDetail view model. When several legs match (reissues) the leg
 *   whose endToEndId equals the searched id is shown, else the latest.
 *
 * Runs against the selected region, or every region in a global search.
 * `data` holds the active region's hit; `hits` carries the rest.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PaymentDetail } from './paymentDetailTypes';
import { mapPaymentDetail } from './paymentDetailMapper';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';
import {
  FanOutResult, RegionLookupState, describeFailures, fanOutByRegion,
  regionBases, regionLookupInitialState,
} from '../regionFanOut';

export interface PaymentDetailState extends RegionLookupState<PaymentDetail> {
  data: PaymentDetail | null;
  /** Other legs returned for the same identifier (reissues), in the active region. */
  otherLegIds: string[];
  loading: boolean;
  error: string | null;
  notFound: boolean;
  currentRequestId: string | null;
  /** Entity id of the last fetch; re-dispatched on region change. */
  currentId: string | null;
}

export const paymentDetailInitialState: PaymentDetailState = {
  data: null,
  otherLegIds: [],
  loading: false,
  error: null,
  notFound: false,
  currentRequestId: null,
  currentId: null,
  ...regionLookupInitialState,
};

export const fetchPaymentDetail = createAsyncThunk<
  FanOutResult<PaymentDetail>, string, { state: RootState; rejectValue: string }
>('paymentDetail/fetch', async (paymentId, { getState }) => {
  const lookup = async (apiBase: string) => {
    const response = await beaconApi.getPaymentDetail([paymentId], apiBase);
    const legs = response.endToEnds ?? [];
    if (legs.length === 0) return { detail: null, otherIds: [] };
    const primary = legs.find(leg => leg.endToEndId === paymentId) ?? legs[legs.length - 1];
    return {
      detail: mapPaymentDetail(primary),
      otherIds: legs.filter(leg => leg !== primary).map(leg => leg.endToEndId),
    };
  };
  return fanOutByRegion(regionBases(getState().env), lookup);
});

/** Points `data` at one region's hit; the sections re-render from it. */
const activate = (state: PaymentDetailState, region: string | null) => {
  const hit = state.hits.find(h => h.region === region) ?? state.hits[0];
  state.activeRegion = hit?.region ?? null;
  state.data = hit?.detail ?? null;
  state.otherLegIds = hit?.otherIds ?? [];
};

const paymentDetailSlice = createSlice({
  name: 'paymentDetail',
  initialState: paymentDetailInitialState,
  reducers: {
    clearPaymentDetail: () => paymentDetailInitialState,
    selectPaymentRegion: (state, action: PayloadAction<string>) => {
      activate(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentDetail.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.notFound = false;
        state.currentRequestId = action.meta.requestId;
        state.currentId = action.meta.arg;
      })
      .addCase(fetchPaymentDetail.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        const { hits, searchedRegions, errors } = action.payload;
        state.loading = false;
        state.hits = hits;
        state.searchedRegions = searchedRegions;
        state.regionErrors = errors;
        activate(state, null);
        // Absence is only proven if every region answered; else it is an error.
        state.notFound = hits.length === 0 && errors.length === 0;
        state.error = hits.length === 0 && errors.length > 0
          ? `No region returned a payment. ${describeFailures(errors)}`
          : null;
      })
      .addCase(fetchPaymentDetail.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch payment detail';
      });
  },
});

export const { clearPaymentDetail, selectPaymentRegion } = paymentDetailSlice.actions;
export default paymentDetailSlice.reducer;
